// src/components/Dashboard/ChecksPanel.jsx
//
// Módulo `checks` de la Fase 4 (pasos "Cobro en curso" y "RCV"): todos los
// cheques del reclamo, cada uno con su propio recorrido (CheckCard), y el
// botón para registrar un cheque nuevo apenas el seguro lo emite (documento
// del cliente: "Seguro emite cheque — llega en 7 a 10 días").
//
// Los costos de appraisal y mediación que se cargaron en la Fase 3 se
// proponen en el breakdown del próximo Closing (se descuentan del
// settlement una sola vez).
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { getChecksForClaimRecord, registerIssuedCheck } from '../../services/claimService';
import { applyCheckRead, buildCheckPrompt, parseCheckAnswer } from '../../services/checkAnalysisService';
import { getStepData } from '../../services/flowService';
import { listClosings } from '../../services/closingService';
import { useAuth } from '../../contexts/AuthContext';
import { PAYMENT_TYPES, money } from '../../documents/closingData';
import CheckCard from './CheckCard';
import CsrCollection from './CsrCollection';
import { localTodayISO } from '../../utils/dateHelpers';

const today = () => localTodayISO();
const millis = t => (t?.toMillis ? t.toMillis() : 0);
const blankForm = claimRecord => ({
  amount: '', checkNumber: '', checkDate: today(), paymentType: '', payableTo: '',
  hasMortgage: Boolean(claimRecord.mortgageCompanyName), adjusterPercentage: claimRecord.agreedFeePercent ?? ''
});

const ChecksPanel = ({ claimRecord, isAdmin, language, onChecksChange }) => {
  const es = language === 'es';
  const { currentUser, userAdjusterName, userCanViewAllClaims, userRoleKey, can } = useAuth();
  const [checks, setChecks] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [prefill, setPrefill] = useState({});
  const [justCreated, setJustCreated] = useState(null);
  const [openLegacy, setOpenLegacy] = useState(null); // cheque anterior al flujo desplegado
  // Rellenar con una IA externa (checkAnalysisService.js): avisos de lo que no se leyó y la respuesta pegada.
  const [readWarnings, setReadWarnings] = useState([]);
  const [promptText, setPromptText] = useState('');
  const [manualAnswer, setManualAnswer] = useState('');

  const publish = list => {
    const sorted = [...list].sort((a, b) => millis(a.createdAt) - millis(b.createdAt));
    setChecks(sorted);
    if (onChecksChange) onChecksChange(sorted);
  };

  useEffect(() => {
    let cancelled = false;
    getChecksForClaimRecord(claimRecord, isAdmin, { name: userAdjusterName, canViewAll: userCanViewAllClaims, summaryOnly: userRoleKey === 'csr_assistant' })
      .then(list => { if (!cancelled) publish(list); })
      .catch(error => { console.error('Error loading checks:', error); if (!cancelled) publish([]); });
    // Costos de la Fase 3 que todavía no se descontaron en ningún Closing.
    Promise.all([
      getStepData('claim', claimRecord.id, 'appraisal').catch(() => ({})),
      getStepData('claim', claimRecord.id, 'mediation').catch(() => ({})),
      listClosings(claimRecord.id).catch(() => [])
    ]).then(([appraisal, mediation, closings]) => {
      if (cancelled) return;
      const already = key => closings.some(c => Number(c[key]) > 0);
      const next = {};
      if (Number(appraisal.appraisalCost) > 0 && !already('appraiserFee')) next.appraiserFee = appraisal.appraisalCost;
      if (Number(mediation.mediationCost) > 0 && !already('mediationFee')) next.mediationFee = mediation.mediationCost;
      setPrefill(next);
    });
    return () => { cancelled = true; };
  }, [claimRecord.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const flowChecks = useMemo(() => (checks || []).filter(c => c.flow?.stepId), [checks]);
  const legacyChecks = useMemo(() => (checks || []).filter(c => !c.flow?.stepId), [checks]);
  const total = (checks || []).reduce((sum, c) => sum + (Number(c.amount) || 0), 0);
  const inProgress = flowChecks.filter(c => c.flow.phase === 'A').length;

  const register = async event => {
    event.preventDefault();
    if (saving) return; // Enter dos veces no crea dos cheques
    if (!(Number(form.amount) > 0)) { toast.error(es ? 'El monto del cheque es obligatorio' : 'Check amount is required'); return; }
    setSaving(true);
    try {
      const created = await registerIssuedCheck(claimRecord, form, currentUser?.email || '');
      publish([...(checks || []), created]);
      setJustCreated(created.id);
      setForm(null);
      setReadWarnings([]); setPromptText(''); setManualAnswer('');
      toast.success(es ? 'Cheque registrado · sigue sus pasos abajo' : 'Check registered · follow its steps below');
    } catch (error) {
      console.error('Error registering check:', error);
      toast.error(es ? 'No se pudo registrar el cheque' : 'Could not register the check');
    } finally {
      setSaving(false);
    }
  };

  const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  // Rellena el formulario con lo leído; lo que ya estaba escrito solo se pisa si la IA sí lo leyó.
  const applyRead = read => {
    setForm(prev => applyCheckRead(prev, read));
    setReadWarnings(read.warnings);
    toast.success(es ? 'Datos cargados: revísalos antes de registrar' : 'Data loaded: review it before registering');
  };
  const copyPrompt = async () => {
    const text = buildCheckPrompt();
    setPromptText(text); // si el portapapeles falla, queda a la vista para copiarlo a mano
    try {
      await navigator.clipboard.writeText(text);
      toast.success(es ? 'Prompt copiado: pégalo en tu IA junto con la foto del cheque' : 'Prompt copied: paste it in your AI with the check photo');
    } catch (error) { toast.error(es ? 'No se pudo copiar solo: cópialo del recuadro.' : 'Could not copy automatically: copy it from the box.'); }
  };
  const applyManualAnswer = () => {
    try { applyRead(parseCheckAnswer(manualAnswer)); setManualAnswer(''); } catch (error) { toast.error(error.message); }
  };

  return (
    <div className="flow-block checks-panel">
      <div className="closing-head">
        <div>
          <div className="closing-head-title">{es ? 'Cheques del seguro' : 'Insurance checks'}</div>
          {checks && checks.length > 0 && (
            <div className="flow-muted">
              {es
                ? `${checks.length} cheque(s) · ${money(total) || '$0'} en total · ${inProgress} en curso`
                : `${checks.length} check(s) · ${money(total) || '$0'} total · ${inProgress} in progress`}
            </div>
          )}
        </div>
        {isAdmin && !form && (
          <button type="button" className="cd-btn cd-btn-check" onClick={() => setForm(blankForm(claimRecord))}>
            <i className="fa-solid fa-plus"></i> {es ? 'Registrar cheque emitido' : 'Register issued check'}
          </button>
        )}
      </div>

      {form && (
        <form className="flow-subpanel" onSubmit={register}>
          <div className="flow-subpanel-title">{es ? 'Nuevo cheque emitido por el seguro' : 'New check issued by the insurance company'}</div>
          <div className="flow-block">
            <div className="flow-block-label"><i className="fa-solid fa-wand-magic-sparkles"></i> {es ? 'Llenar con tu IA (Gemini, ChatGPT…)' : 'Fill with your AI (Gemini, ChatGPT…)'}</div>
            <ol className="policy-manual-steps">
              <li>{es ? 'Copia el prompt.' : 'Copy the prompt.'} <button type="button" className="cd-btn" onClick={copyPrompt}><i className="fa-solid fa-copy"></i> {es ? 'Copiar prompt' : 'Copy prompt'}</button>
                {promptText && <textarea className="cc-input" rows={4} readOnly value={promptText} aria-label={es ? 'Prompt para copiar' : 'Prompt to copy'} onFocus={e => e.target.select()} />}</li>
              <li>{es ? 'En tu IA, adjunta la foto o el PDF del cheque y pega el prompt.' : 'In your AI, attach the check photo or PDF and paste the prompt.'}</li>
              <li>{es ? 'Pega aquí la respuesta (el JSON) y pulsa Aplicar: llena los campos de abajo.' : 'Paste the answer (the JSON) here and press Apply: it fills the fields below.'}
                <textarea className="cc-input" rows={4} value={manualAnswer} onChange={e => setManualAnswer(e.target.value)} aria-label={es ? 'Respuesta de la IA' : 'AI answer'} />
                <button type="button" className="cd-btn cd-btn-check" disabled={!manualAnswer.trim()} onClick={applyManualAnswer}>{es ? 'Aplicar' : 'Apply'}</button></li>
            </ol>
            <p className="flow-note">{es ? 'Ojo: el cheque tiene datos personales; usa una cuenta de trabajo de la IA, no una personal.' : 'Note: the check has personal data; use a work AI account, not a personal one.'}</p>
          </div>
          {readWarnings.length > 0 && <ul className="closing-warning policy-warnings">{readWarnings.map((w, i) => <li key={i}>{w}</li>)}</ul>}
          <div className="cc-grid">
            <div className="cc-input-group">
              <label className="cc-label">{es ? 'Monto del cheque *' : 'Check amount *'}</label>
              <input type="number" step="0.01" min="0" className="cc-input" value={form.amount} onChange={e => set('amount', e.target.value)} required />
            </div>
            <div className="cc-input-group">
              <label className="cc-label">{es ? 'Número del cheque' : 'Check number'}</label>
              <input className="cc-input" value={form.checkNumber} onChange={e => set('checkNumber', e.target.value)} placeholder={es ? 'Si ya lo sabes' : 'If known'} />
            </div>
            <div className="cc-input-group">
              <label className="cc-label">{es ? 'Fecha de emisión' : 'Issue date'}</label>
              <input type="date" className="cc-input" value={form.checkDate} onChange={e => set('checkDate', e.target.value)} />
            </div>
            <div className="cc-input-group">
              <label className="cc-label">{es ? 'Tipo de pago' : 'Payment type'}</label>
              <select className="cc-select" value={form.paymentType} onChange={e => set('paymentType', e.target.value)}>
                <option value="">—</option>
                {PAYMENT_TYPES.map(p => <option key={p.value} value={p.value}>{es ? p.es : p.value}</option>)}
              </select>
            </div>
            <div className="cc-input-group">
              <label className="cc-label">{es ? 'A nombre de (payable to)' : 'Payable to'}</label>
              <input className="cc-input" value={form.payableTo} onChange={e => set('payableTo', e.target.value)} />
            </div>
            <div className="cc-input-group">
              <label className="cc-label">{es ? '% del PA' : 'PA %'}</label>
              <input type="number" step="0.5" min="0" max="100" className="cc-input" value={form.adjusterPercentage} onChange={e => set('adjusterPercentage', e.target.value)} />
            </div>
          </div>
          <label className="check-toggle">
            <input type="checkbox" checked={form.hasMortgage} onChange={e => set('hasMortgage', e.target.checked)} />
            {es ? 'El cheque incluye al mortgage (necesita endoso del banco)' : 'The check includes the mortgage (needs bank endorsement)'}
            {claimRecord.mortgageCompanyName && <span className="flow-muted"> · {claimRecord.mortgageCompanyName}</span>}
          </label>
          <div className="flow-actions">
            <button type="submit" className="cd-btn cd-btn-check" disabled={saving}><i className="fa-solid fa-floppy-disk"></i> {saving ? (es ? 'Guardando…' : 'Saving…') : (es ? 'Registrar cheque' : 'Register check')}</button>
            <button type="button" className="cd-btn" onClick={() => setForm(null)}>{es ? 'Cancelar' : 'Cancel'}</button>
          </div>
        </form>
      )}

      {checks === null && <p className="flow-note">{es ? 'Cargando cheques…' : 'Loading checks…'}</p>}
      {checks && checks.length === 0 && !form && (
        <p className="flow-empty">
          <i className="fa-solid fa-money-check-dollar"></i>
          {es ? 'Todavía no hay cheques. Cuando el seguro emita uno, regístralo aquí y sigue sus pasos.' : 'No checks yet. When the insurance company issues one, register it here and follow its steps.'}
        </p>
      )}
      {Object.keys(prefill).length > 0 && flowChecks.some(c => c.flow.phase === 'A') && (
        <p className="flow-note">
          <i className="fa-solid fa-circle-info"></i> {es
            ? `Se propondrán en el próximo breakdown: ${[prefill.appraiserFee && `Appraisal ${money(prefill.appraiserFee)}`, prefill.mediationFee && `Mediación ${money(prefill.mediationFee)}`].filter(Boolean).join(' · ')}.`
            : `Proposed in the next breakdown: ${[prefill.appraiserFee && `Appraisal ${money(prefill.appraiserFee)}`, prefill.mediationFee && `Mediation ${money(prefill.mediationFee)}`].filter(Boolean).join(' · ')}.`}
        </p>
      )}

      <div className="checks-list">
        {flowChecks.map(check => (
          <CheckCard key={check.id} check={check} claimRecord={claimRecord} isAdmin={isAdmin} language={language} prefill={prefill}
            defaultOpen={check.id === justCreated || (flowChecks.length === 1 && check.flow.phase === 'A')}
            onChange={updated => publish(checks.map(c => (c.id === updated.id ? updated : c)))} />
        ))}
      </div>

      {legacyChecks.length > 0 && (
        <div className="flow-block">
          <div className="flow-block-label">{es ? 'Cheques anteriores al flujo de pasos' : 'Checks before the step flow'}</div>
          <div className="checks-list">
            {legacyChecks.map(check => {
              const isOpen = openLegacy === check.id;
              return (
                <div key={check.id} className={`check-card check-card-legacy ${isOpen ? 'is-open' : ''}`}>
                  <button type="button" className="check-card-head" aria-expanded={isOpen} onClick={() => setOpenLegacy(isOpen ? null : check.id)}>
                    <span className="check-card-icon"><i className="fa-solid fa-money-check"></i></span>
                    <span className="check-card-main">
                      <span className="check-card-title">{money(check.amount) || '$0'}{can('viewEarnings') && ` · ${check.invoiceNumber ? `Invoice ${check.invoiceNumber}` : (es ? 'Sin invoice' : 'No invoice')}`}</span>
                      <span className="check-card-meta">{[check.checkDate, can('viewEarnings') && (check.paid || check.archived ? (es ? 'Comisión cobrada' : 'Commission collected') : (es ? 'Cobro CSR pendiente' : 'CSR collection pending'))].filter(Boolean).join(' · ')}</span>
                    </span>
                    <i className={`fa-solid fa-chevron-${isOpen ? 'up' : 'down'} flow-step-chevron`}></i>
                  </button>
                  {isOpen && (
                    <div className="check-card-body">
                      <CsrCollection check={check} isAdmin={isAdmin} language={language}
                        onCheckChange={fresh => publish(checks.map(c => (c.id === check.id ? { ...c, ...fresh } : c)))} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChecksPanel;
